import { IconButton } from "@lcc/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import type { GeoJSONSource, Map as MlMap, Marker as MlMarker, StyleSpecification } from "maplibre-gl";
import { useFireContext } from "../context";
import { gridStyle, LUSAKA_CENTER, resolveBasemap, type BasemapColours, type ResolvedBasemap } from "./basemap";
import { allPoints, bbox, layerSpecs, SOURCE_IDS, sourceData, type LngLat, type MapData, type Palette } from "./layers";

export type PickKind = "incident" | "alert" | "station" | "appliance" | "candidate" | "hydrant";

export interface FireMapProps {
  /** Accessible name of the map region; say which table on the screen has the same data. */
  ariaLabel: string;
  data: MapData;
  onPick?: (kind: PickKind, id: string) => void;
  /** While dropping a pin, map clicks place it instead of picking. */
  pinMode?: boolean;
  onPin?: (lngLat: LngLat) => void;
  /** Words for the legend of what the basemap is (reported once resolved). */
  onBasemap?: (b: Pick<ResolvedBasemap, "kind" | "label">) => void;
  height?: number;
  /** Fill the positioned parent (map-first pages: the map is the page, panels float over it). */
  fill?: boolean;
  /** Room kept free when fitting the points (map-first pages: the floating rail and inspector cover the map's sides). */
  fitPadding?: { top: number; right: number; bottom: number; left: number };
}

function webglAvailable(): boolean {
  try {
    const c = document.createElement("canvas");
    return !!(c.getContext("webgl2") || c.getContext("webgl"));
  } catch {
    return false;
  }
}

/** Read a design token (CSS custom property) as a colour MapLibre can use. */
function token(el: HTMLElement, name: string): string {
  return getComputedStyle(el).getPropertyValue(name).trim() || "currentColor";
}

function palette(el: HTMLElement): Palette & BasemapColours {
  return {
    bg: token(el, "--sc-bg"),
    surface: token(el, "--sc-surface"),
    border: token(el, "--sc-border"),
    text: token(el, "--sc-text"),
    muted: token(el, "--sc-text-muted"),
    primary: token(el, "--sc-primary"),
    accent: token(el, "--sc-accent"),
    warning: token(el, "--sc-warning"),
    warningText: token(el, "--sc-warning-text"),
    danger: token(el, "--sc-danger"),
  };
}

interface Label {
  key: string;
  at: LngLat;
  text: string;
  aria: string;
  className: string;
  onClick?: () => void;
}

let protocolAdded = false;

/**
 * MapLibre map of the console: wards, incidents (size by severity, critical in red), unhandled alerts (rings, SOS in
 * red), refused SOS (coarse dashed squares), stations, appliances by status, hydrants, an alert's fixes and duplicate
 * candidates, and a dropped pin. Incidents, alerts, stations, appliances and candidates get HTML labels that are
 * buttons with a text name, so everything the map offers is reachable by keyboard; the screens list the same data in
 * tables. MapLibre and PMTiles load only when a map is shown. The basemap is the council's tiles, the console's
 * offline PMTiles archive, or a plain grid when tiles are unavailable.
 */
export function FireMap({ ariaLabel, data, onPick, pinMode = false, onPin, onBasemap, height = 460, fill = false, fitPadding }: FireMapProps) {
  const { basemap } = useFireContext();
  const container = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  const markersRef = useRef<MlMarker[]>([]);
  const libRef = useRef<typeof import("maplibre-gl") | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "unavailable">("loading");
  const [base, setBase] = useState<Pick<ResolvedBasemap, "kind" | "label"> | null>(null);
  const handlers = useRef({ onPick, pinMode, onPin, onBasemap });
  const padRef = useRef(fitPadding);
  padRef.current = fitPadding;
  handlers.current = { onPick, pinMode, onPin, onBasemap };
  const { styleUrl, pmtilesUrl } = basemap;

  useEffect(() => {
    if (!container.current) return;
    if (!webglAvailable()) {
      setState("unavailable");
      return;
    }
    let cancelled = false;
    const el = container.current;
    Promise.all([import("maplibre-gl"), import("pmtiles"), import("maplibre-gl/dist/maplibre-gl.css")])
      .then(async ([lib, pm]) => {
        if (cancelled) return;
        const maplibre = (lib as unknown as { default?: typeof import("maplibre-gl") }).default ?? lib;
        libRef.current = maplibre;
        if (!protocolAdded) {
          const protocol = new pm.Protocol();
          maplibre.addProtocol("pmtiles", protocol.tile);
          protocolAdded = true;
        }
        const colours = palette(el);
        const pmProbe = async (url: string) => {
          const header = await new pm.PMTiles(url).getHeader();
          return { tileType: header.tileType };
        };
        const styleProbe = async (url: string) => (await fetch(url, { cache: "no-store" })).ok;
        const resolved = await resolveBasemap({ styleUrl, pmtilesUrl }, colours, pmProbe, styleProbe);
        if (cancelled) return;
        const report = (b: Pick<ResolvedBasemap, "kind" | "label">) => {
          setBase(b);
          handlers.current.onBasemap?.(b);
        };
        report(resolved);
        const map = new maplibre.Map({ container: el, style: resolved.style, center: LUSAKA_CENTER, zoom: 11, attributionControl: false });
        let loaded = false;
        // The council's tiles failed before the first render (gis down): the offline archive, else the grid.
        let fellBack = false;
        map.on("error", () => {
          if (!loaded && resolved.kind === "style" && !fellBack) {
            fellBack = true;
            void resolveBasemap({ pmtilesUrl }, colours, pmProbe).then((next) => {
              if (cancelled) return;
              map.setStyle((next.kind === "grid" ? gridStyle(colours) : next.style) as StyleSpecification);
              report(next.kind === "grid" ? { kind: "grid", label: "Map tiles unavailable: plain grid (every 0.05°)" } : next);
            });
          }
        });
        const onStyle = () => {
          for (const id of SOURCE_IDS) if (!map.getSource(id)) map.addSource(id, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
          for (const layer of layerSpecs(colours)) if (!map.getLayer(layer.id)) map.addLayer(layer);
          mapRef.current = map;
          setState("ready");
        };
        map.on("load", () => {
          loaded = true;
          onStyle();
        });
        map.on("styledata", () => {
          if (loaded && !map.getSource("incidents")) onStyle();
        });
        map.on("click", (e) => {
          const h = handlers.current;
          if (h.pinMode) {
            h.onPin?.([e.lngLat.lng, e.lngLat.lat]);
            return;
          }
          const f = map.queryRenderedFeatures(e.point, { layers: ["hydrants"] })[0];
          const id = f?.properties?.id;
          if (typeof id === "string") h.onPick?.("hydrant", id);
        });
      })
      .catch(() => !cancelled && setState("unavailable"));
    return () => {
      cancelled = true;
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, [styleUrl, pmtilesUrl]);

  const sources = useMemo(() => sourceData(data), [data]);
  useEffect(() => {
    const map = mapRef.current;
    if (state !== "ready" || !map) return;
    for (const id of SOURCE_IDS) (map.getSource(id) as GeoJSONSource | undefined)?.setData(sources[id]);
    map.getCanvas().style.cursor = pinMode ? "crosshair" : "";
  }, [sources, state, pinMode]);

  const points = useMemo(() => allPoints(data), [data]);
  const fitKey = points.length === 0 ? "" : `${points.length}:${points[0].join(",")}`;
  useEffect(() => {
    const map = mapRef.current;
    if (state !== "ready" || !map) return;
    const b = bbox(points);
    // The floating panels' room only when the map is wide and tall enough to keep some of it (stacked on a phone).
    const pad = padRef.current;
    const box = map.getContainer();
    const fits = pad && box.clientWidth > pad.left + pad.right + 160 && box.clientHeight > pad.top + pad.bottom + 160;
    if (b) map.fitBounds(b, { padding: fits ? pad : 40, maxZoom: 15, duration: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fit only when the set of points changes
  }, [fitKey, state]);

  const labels = useMemo<Label[]>(() => {
    const pick = (kind: PickKind, id: string) => () => handlers.current.onPick?.(kind, id);
    return [
      ...(data.stations ?? []).map((s) => ({
        key: `s/${s.id}`,
        at: s.point,
        text: `${s.code} ⌂`,
        aria: `Station ${s.code} ${s.name}`,
        className: "fer-map-label fer-map-label--station",
        onClick: pick("station", s.id),
      })),
      ...(data.appliances ?? []).map((a) => ({
        key: `a/${a.id}`,
        at: a.point,
        text: `${a.callSign}${a.stale ? " (old position)" : ""}`,
        aria: `Appliance ${a.callSign}, ${a.status.replace(/_/g, " ")}${a.stale ? ", position is old" : ""}`,
        className: `fer-map-label${a.stale ? " fer-map-label--stale" : ""}`,
        onClick: pick("appliance", a.id),
      })),
      ...(data.alerts ?? []).map((a) => ({
        key: `al/${a.id}`,
        at: a.point,
        text: `${a.channel === "app_sos" ? "SOS " : ""}${a.reference} ○`,
        aria: `Unhandled alert ${a.reference}${a.channel === "app_sos" ? " (SOS from the app)" : ""}`,
        className: `fer-map-label fer-map-label--alert${a.channel === "app_sos" ? " fer-map-label--sos" : ""}${a.id === data.selectedId ? " fer-map-label--selected" : ""}`,
        onClick: pick("alert", a.id),
      })),
      ...(data.candidates ?? []).map((c) => ({
        key: `c/${c.id}`,
        at: c.point,
        text: `${c.reference} ≈`,
        aria: `Possible duplicate: ${c.kind} ${c.reference}`,
        className: "fer-map-label fer-map-label--candidate",
        onClick: pick("candidate", c.id),
      })),
      ...(data.incidents ?? []).map((i) => ({
        key: `i/${i.id}`,
        at: i.point,
        text: `${i.reference} ${i.severity === "critical" ? "‼" : "●"}${i.backup ? " +backup" : ""}`,
        aria: `Incident ${i.reference}, ${i.typeLabel}, ${i.severity} severity, ${i.status.replace(/_/g, " ")}${i.backup ? ", backup requested" : ""}${i.id === data.selectedId ? ", selected" : ""}`,
        className: `fer-map-label${i.severity === "critical" ? " fer-map-label--critical" : ""}${i.id === data.selectedId ? " fer-map-label--selected" : ""}`,
        onClick: pick("incident", i.id),
      })),
    ];
  }, [data.stations, data.appliances, data.alerts, data.candidates, data.incidents, data.selectedId]);

  useEffect(() => {
    const map = mapRef.current;
    const lib = libRef.current;
    if (state !== "ready" || !map || !lib) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = labels.map((l) => {
      const el = document.createElement(l.onClick ? "button" : "span");
      if (l.onClick) (el as HTMLButtonElement).type = "button";
      el.className = l.className;
      el.textContent = l.text;
      el.setAttribute("aria-label", l.aria);
      if (l.onClick) {
        const click = l.onClick;
        el.addEventListener("click", (ev) => {
          ev.stopPropagation();
          click();
        });
      }
      return new lib.Marker({ element: el, anchor: "bottom", offset: [0, -10] }).setLngLat(l.at).addTo(map);
    });
  }, [labels, state]);

  return (
    <div className={`fer-map-wrap${fill ? " fer-map-wrap--fill" : ""}`}>
      <div ref={container} className={`fer-map${pinMode ? " fer-map--pin" : ""}`} style={fill ? undefined : { height }} role="region" aria-label={ariaLabel} />
      {state === "ready" && (
        <div className="fer-map-zoom">
          <IconButton shape="map" label="Zoom in" icon={<span aria-hidden="true">+</span>} onClick={() => mapRef.current?.zoomIn()} />
          <IconButton shape="map" label="Zoom out" icon={<span aria-hidden="true">−</span>} onClick={() => mapRef.current?.zoomOut()} />
        </div>
      )}
      {base && (
        <p className="fer-map-basemap">
          Basemap: {base.label}
        </p>
      )}
      {state === "unavailable" && (
        <p className="fer-map-fallback" role="status">
          The map cannot be shown on this device (WebGL is unavailable). The tables on this screen hold the same data and
          every action; a location can be typed as longitude and latitude.
        </p>
      )}
    </div>
  );
}
